import {
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Icon,
  IconButton,
} from "@mui/material";
import FormField from "components/FormField/FormField";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import timeEndAdornment from "constants/timeEndAdornment";
import dayjs, { Dayjs } from "dayjs";
import { Job, ScheduleJobInput } from "generated/graphql";
import useScheduleJob from "hooks/jobs/useScheduleJob";
import useGetJobLazy from "hooks/jobs/useGetJobLazy";
import useGetAllCrews from "hooks/crews/useGetAllCrews";
import useGetOrganizationSettings from "hooks/organization/useGetOrganizationSettings";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { omit } from "ramda";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import { convertHexToRGB } from "utils/object/getRandomColor";
import { ColorPickerProps } from "modules/OrganizationCrews/Create";
import SelectCrewOrIndividual from "modules/SelectCrewOrIndividual/SelectCrewOrIndividual";
import generateBlocksForJob from "utils/jobs/generateBlocksForJob";

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const DEFAULT_INDIVIDUAL_COLOR = "#B3B3B3";

type ScheduleJobFormValues = Omit<ScheduleJobInput, "blocks"> & {
  assignTo?: string;
  blocks: {
    scheduledAt: string;
    blockLength: number;
    showTravelTo: boolean;
    showTravelFrom: boolean;
  }[];
};

function calculateHoursTotal(blocks: { blockLength: number }[]) {
  if (!blocks || !Array.isArray(blocks)) {
    return 0;
  }

  return blocks.reduce((acc, val) => {
    return +acc + +val.blockLength;
  }, 0);
}

interface CustomInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  value?: string;
  onClick?: () => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const CustomInput = forwardRef<HTMLInputElement, CustomInputProps>(
  ({ value, onClick, onChange, ...props }, ref) => (
    <div style={{ position: "relative", width: "200px" }}>
      <input
        {...props}
        value={value}
        onChange={onChange}
        onClick={onClick}
        ref={ref}
        className="date-input"
        style={{ width: "100%", paddingRight: "30px" }}
      />
      <Icon
        sx={{
          position: "absolute",
          right: "8px",
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
          fontSize: "1.2rem",
          color: "#344767",
        }}
      >
        calendar_today
      </Icon>
    </div>
  )
);

CustomInput.displayName = "CustomInput";

interface ScheduleJobProps {
  jobId: Job["id"];
  onClose: () => void;
  onScheduleSuccess?: () => void;
  initialScheduledAt?: Date | null;
  initialDuration?: number | null;
}

function HoursTotal({ maxPhaseDuration }: { maxPhaseDuration: number }) {
  const { watch } = useFormContext();
  const blocks = watch(`blocks`);

  const hoursTotal = useMemo(() => {
    return calculateHoursTotal(blocks);
  }, [blocks]);

  return (
    <MDTypography variant="body2">
      {hoursTotal} hours scheduled (longest phase: {maxPhaseDuration.toFixed(2)} hours)
    </MDTypography>
  );
}

function ScheduleJobForm({
  job,
  jobId,
  organizationSettings,
  onClose,
  onScheduleSuccess,
  initialScheduledAt,
  initialDuration,
}: {
  job: any;
  jobId: Job["id"];
  organizationSettings: Record<string, string>;
  onClose: () => void;
  onScheduleSuccess?: () => void;
  initialScheduledAt?: Date | null;
  initialDuration?: number | null;
}) {
  const [scheduleJob, { loading: scheduling, isSuccess }] = useScheduleJob();
  const { show } = useGlobalMessage();

  const maxPhaseDuration = useMemo(() => {
    if (!job?.jobPhases) return initialDuration || 0;
    const unscheduledPhases = job.jobPhases.filter(
      (phase: any) => phase.status === "TO_BE_SCHEDULED"
    );
    const durations = unscheduledPhases.map((phase: any) => phase.jobDuration || 0);
    return durations.length > 0 ? Math.max(...durations) : 0;
  }, [job?.jobPhases, initialDuration]);

  const unscheduledPhases = useMemo(() => {
    if (!job?.jobPhases) return [];
    return job.jobPhases.filter((phase: any) => phase.status === "TO_BE_SCHEDULED");
  }, [job?.jobPhases]);

  const singleCrewId = useMemo(() => {
    if (job?.crew?.id) {
      return job.crew.id;
    }
    return null;
  }, [job]);

  const [showTravelTime, setShowTravelTime] = useState(false);
  const [travelTimeRecalculating, setTravelTimeRecalculating] = useState(true);
  const hasResetForm = useRef(false);

  const defaultColor = DEFAULT_INDIVIDUAL_COLOR;
  const [colorPicker, setColorPicker] = useState<ColorPickerProps>({
    displayColorPicker: false,
    color: {
      hex: defaultColor,
      rgb: convertHexToRGB(defaultColor),
    },
  });

  const defaultBlocks = useMemo(() => {
    const blocks = generateBlocksForJob({
      maxPhaseDuration,
      organizationSettings,
      showTravelTo: false,
      showTravelFrom: false,
    });

    // If there's an initial scheduled time (from drag-drop), set it on the first block
    if (initialScheduledAt && blocks.length > 0) {
      blocks[0].scheduledAt = dayjs(initialScheduledAt).format("YYYY-MM-DD HH:mm:ss");
    }

    return blocks;
  }, [initialScheduledAt, maxPhaseDuration, organizationSettings]);

  const methods = useForm<ScheduleJobFormValues>({
    defaultValues: {
      jobId,
      crewId: undefined,
      userIDs: [],
      assignTo: "",
      usersColor: undefined,
      travelTimeEstimate: undefined,
      blocks: [],
    },
  });

  const {
    formState: { errors },
    register,
  } = methods;

  useEffect(() => {
    if (hasResetForm.current) return;

    const timeout = setTimeout(() => {
      if (defaultBlocks.length > 0) {
        hasResetForm.current = true;
        methods.reset({
          jobId,
          crewId: undefined,
          userIDs: [],
          assignTo: "",
          usersColor: undefined,
          travelTimeEstimate: undefined,
          blocks: defaultBlocks,
        });
        setTravelTimeRecalculating(false);
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [defaultBlocks, jobId, methods.reset]);

  const watchedCrewId = methods.watch("crewId");
  const watchedUserIDs = methods.watch("userIDs");

  useEffect(() => {
    if (watchedCrewId) {
      methods.clearErrors("crewId");
    }
    if (watchedUserIDs && watchedUserIDs.length > 0) {
      methods.clearErrors("userIDs");
    }
  }, [watchedCrewId, watchedUserIDs, methods]);

  const handleShowTravelTimeChange = useCallback(
    (value: boolean) => {
      setTravelTimeRecalculating(true);
      setTimeout(() => {
        const { blocks } = methods.getValues();
        const updatedBlocks = blocks.map((block) => ({
          ...block,
          showTravelFrom: value,
          showTravelTo: value,
        }));
        methods.setValue("blocks", updatedBlocks);
        methods.setValue("travelTimeEstimate", value ? undefined : undefined);
        setShowTravelTime(value);
        setTravelTimeRecalculating(false);
      }, 300);
    },
    [methods]
  );

  const onSubmit = async (values: ScheduleJobFormValues) => {
    if (!values.assignTo) {
      methods.setError("assignTo", {
        type: "manual",
        message: "Please select a value",
      });
      return;
    }

    if (values.assignTo === "crew" && !values.crewId) {
      methods.setError("crewId", {
        type: "manual",
        message: "Please select a value",
      });
      return;
    }

    if (values.assignTo === "individual" && (!values.userIDs || values.userIDs.length === 0)) {
      methods.setError("userIDs", {
        type: "manual",
        message: "Please select a value",
      });
      return;
    }

    const formattedPayload: ScheduleJobInput = {
      ...omit(["assignTo"], values),
      usersColor: colorPicker.color.hex.substring(1),
      blocks: values.blocks.map((block) => ({
        blockLength: block.blockLength,
        scheduledAt: dayjs(block.scheduledAt).utc().format("YYYY-MM-DD HH:mm:ss"),
        showTravelTo: block.showTravelTo,
        showTravelFrom: block.showTravelFrom,
      })),
    };

    const result = await scheduleJob({
      variables: {
        input: formattedPayload,
      },
    });

    if (result.success && isSuccess(result.data)) {
      onScheduleSuccess?.();
      onClose();
      show({ message: "Job scheduled successfully" });
    } else {
      let errorMessage = "Failed to schedule job";
      if ((result as any).graphQLErrors?.[0]?.message?.includes("unauthorized")) {
        errorMessage = "Insufficient Permissions";
      }
      show({ message: errorMessage, options: { variant: "error" } });
    }
  };

  return (
    <MDBox
      sx={{
        width: "900px",
        maxWidth: "100%",
        maxHeight: "90vh",
        overflow: "auto",
        p: 2,
      }}
    >
      {travelTimeRecalculating && (
        <MDBox
          sx={{
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(50, 50, 50, 0.2)",
            position: "absolute",
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            zIndex: 2,
          }}
        >
          <CircularProgress color="inherit" size={40} thickness={4} value={99} />
        </MDBox>
      )}

      <MDTypography variant="h6" mb={2}>
        Schedule Entire Job
      </MDTypography>

      <MDBox mb={2} sx={{ backgroundColor: "#f8f9fa", p: 1.5, borderRadius: 1 }}>
        <MDTypography variant="body2" fontWeight="medium">
          Job ID: {job?.externalId || jobId}
        </MDTypography>
        <MDTypography variant="caption" color="text.secondary">
          {job?.address?.line1 || "N/A"}
        </MDTypography>
        <MDTypography variant="caption" color="text.secondary" display="block">
          {unscheduledPhases.length} phase(s) to schedule
        </MDTypography>
      </MDBox>

      {job && unscheduledPhases.length === 0 ? (
        <MDAlert color="info">All phases are already scheduled for this job.</MDAlert>
      ) : (
        <FormProvider {...methods}>
          <MDBox display="flex" width="100%" justifyContent="center" mb={2}>
            <HoursTotal maxPhaseDuration={maxPhaseDuration} />
          </MDBox>

          <MDBox component="form" role="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <MDBox mb={3}>
              <SelectCrewOrIndividual
                colorPicker={colorPicker}
                setColorPicker={setColorPicker}
                methods={methods}
                errors={errors}
                singleCrewId={singleCrewId}
              />
              {errors.assignTo && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.assignTo.message}
                </FormHelperText>
              )}
            </MDBox>

            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
              <Checkbox
                checked={showTravelTime}
                onChange={(e) => handleShowTravelTimeChange(e.target.checked)}
              />
              <MDTypography variant="button" fontWeight="bold">
                Show Travel Time
              </MDTypography>
            </MDBox>

            {showTravelTime && (
              <MDBox
                sx={{
                  textAlign: "center",
                  mb: 2,
                  width: "50%",
                }}
              >
                <FormField
                  placeholder="Travel Time estimate"
                  mb={0}
                  align="right"
                  {...timeEndAdornment}
                  {...register("travelTimeEstimate", { valueAsNumber: true })}
                />
              </MDBox>
            )}

            <Divider sx={{ width: "100%", my: 2 }} />

            <ScheduleBlocks showTravelTime={showTravelTime} />

            <MDBox display="flex" justifyContent="flex-end" gap={1} mt={3}>
              <MDButton variant="outlined" color="secondary" onClick={onClose}>
                Cancel
              </MDButton>
              <MDButton variant="gradient" color="info" type="submit" disabled={scheduling}>
                {scheduling ? <CircularProgress size={16} color="inherit" /> : "Schedule Job"}
              </MDButton>
            </MDBox>
          </MDBox>
        </FormProvider>
      )}
    </MDBox>
  );
}

const ScheduleBlocks = React.memo(({ showTravelTime }: { showTravelTime: boolean }) => {
  const { control } = useFormContext<ScheduleJobFormValues>();

  const {
    fields,
    append: appendBlock,
    remove,
  } = useFieldArray({
    control,
    name: "blocks",
  });

  const handleRemoveScheduleBlock = useCallback(
    (blockIndex: number) => {
      if (fields.length === 1) {
        // Do not remove if there is only one field left
        return;
      }
      remove(blockIndex);
    },
    [fields.length, remove]
  );

  const handleAppendBlock = useCallback(
    (startDate: Date) => {
      const lastBlock = fields[fields.length - 1];
      const baseDate = lastBlock?.scheduledAt ? dayjs(lastBlock.scheduledAt) : dayjs(startDate);
      const nextDate = baseDate.add(1, "day").format("YYYY-MM-DD HH:mm:ss");

      appendBlock({
        scheduledAt: nextDate,
        blockLength: (lastBlock as any)?.blockLength || 8,
        showTravelTo: showTravelTime,
        showTravelFrom: showTravelTime,
      });
    },
    [fields, showTravelTime, appendBlock]
  );

  return (
    <MDBox>
      <Grid container py={1}>
        {fields.map((field, i) => (
          <Grid item key={field.id} xs={12}>
            <ScheduleBlock
              index={i}
              onRemoveScheduleBlock={handleRemoveScheduleBlock}
              showTravelTime={showTravelTime}
              onAppendBlock={handleAppendBlock}
            />
            {i < fields.length - 1 && <Divider sx={{ my: 1 }} />}
          </Grid>
        ))}
      </Grid>
    </MDBox>
  );
});

ScheduleBlocks.displayName = "ScheduleBlocks";

const ScheduleBlock = React.memo(
  ({
    index,
    onRemoveScheduleBlock,
    showTravelTime,
    onAppendBlock,
  }: {
    index: number;
    onRemoveScheduleBlock: (index: number) => void;
    showTravelTime: boolean;
    onAppendBlock: (startDate: Date) => void;
  }) => {
    const { control, getValues } = useFormContext<ScheduleJobFormValues>();
    const [endDate, setEndDate] = useState<Dayjs | null>(null);
    const [startDate, setStartDate] = useState(new Date());

    useEffect(() => {
      const { blocks } = getValues();
      const block = blocks[index];
      if (!block?.scheduledAt || !block?.blockLength) {
        setEndDate(null);
      } else {
        setEndDate(dayjs(block.scheduledAt).add(block.blockLength, "hours"));
        setStartDate(new Date(block.scheduledAt));
      }
    }, [getValues, index]);

    const recalculateEndDate = useCallback(() => {
      const { blocks } = getValues();
      const block = blocks[index];
      if (!block?.scheduledAt || !block?.blockLength) {
        setEndDate(null);
      } else {
        setEndDate(dayjs(block.scheduledAt).add(block.blockLength, "hours"));
      }
    }, [getValues, index]);

    return (
      <>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <Controller
              control={control}
              name={`blocks.${index}.scheduledAt`}
              render={({ field, fieldState: { error } }) => (
                <MDBox
                  display="flex"
                  flexDirection="column"
                  sx={{
                    ".date-input": {
                      height: "32px",
                      width: "200px",
                      fontSize: "0.875rem",
                      border: "none",
                      borderRadius: 0,
                      borderBottom: "1px solid #7b809a",
                      color: "#344767",
                      padding: "0 !important",
                      "&:focus-visible": {
                        outline: "none",
                      },
                    },
                  }}
                >
                  <label
                    htmlFor={`blocks.${index}.scheduledAt`}
                    style={{ fontSize: "0.875rem", color: "#7b809a" }}
                  >
                    Start Date
                  </label>
                  <DatePicker
                    name={`blocks.${index}.scheduledAt`}
                    {...field}
                    selected={startDate}
                    shouldCloseOnSelect={false}
                    onChange={(e) => {
                      field.onChange(dayjs(e).format("YYYY-MM-DD HH:mm:ss"));
                      setStartDate(e as Date);
                      recalculateEndDate();
                    }}
                    ref={(ref) => {
                      field.ref({
                        focus: ref?.setFocus,
                      });
                    }}
                    showTimeSelect
                    timeIntervals={15}
                    dateFormat="yyyy-MM-dd HH:mm:ss"
                    customInput={<CustomInput />}
                  />
                  {error && <FormHelperText error>{error.message}</FormHelperText>}
                </MDBox>
              )}
            />
            <MDTypography variant="body2" my={1} fontSize="0.875rem">
              End Date: {endDate && endDate.format("LLL")}
            </MDTypography>
          </Grid>
          <Grid item xs={4}>
            <Controller
              control={control}
              name={`blocks.${index}.blockLength`}
              render={({ field, fieldState: { error } }) => (
                <FormField
                  label="Hours"
                  inputProps={{ type: "number", step: "any", min: "0.01" }}
                  {...field}
                  value={field.value || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(parseFloat(e.target.value) || 0);
                    recalculateEndDate();
                  }}
                  error={error}
                />
              )}
            />
          </Grid>
          <Grid item xs={2}>
            <MDBox display="flex">
              <IconButton onClick={() => onRemoveScheduleBlock(index)}>
                <Icon>remove_circle_outline</Icon>
              </IconButton>
              <IconButton onClick={() => onAppendBlock(startDate)}>
                <Icon>add_circle_outline</Icon>
              </IconButton>
            </MDBox>
          </Grid>
        </Grid>
        {showTravelTime && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name={`blocks.${index}.showTravelTo`}
                render={({ field }) => (
                  <FormGroup sx={{ pl: 1 }}>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Show To Travel Time"
                    />
                  </FormGroup>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                control={control}
                name={`blocks.${index}.showTravelFrom`}
                render={({ field }) => (
                  <FormGroup>
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Show From Travel Time"
                    />
                  </FormGroup>
                )}
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  }
);

ScheduleBlock.displayName = "ScheduleBlock";

export default function ScheduleJob({
  jobId,
  onClose,
  onScheduleSuccess,
  initialScheduledAt,
  initialDuration,
}: ScheduleJobProps) {
  const [getJob, { job, loading: loadingJob }] = useGetJobLazy();
  const { data: crews, loading: loadingCrews } = useGetAllCrews();
  const { loading: loadingOrganizationSettings, data: organizationSettings } =
    useGetOrganizationSettings();

  const hasFetchedJob = useRef(false);

  useEffect(() => {
    if (jobId && !hasFetchedJob.current) {
      hasFetchedJob.current = true;
      getJob(jobId);
    }
  }, [jobId, getJob]);

  if (loadingJob || loadingCrews || loadingOrganizationSettings || !job) {
    return (
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        width="900px"
        maxWidth="100%"
        height="200px"
      >
        <CircularProgress color="inherit" size={24} />
      </MDBox>
    );
  }

  return (
    <ScheduleJobForm
      key={job.id}
      job={job}
      jobId={jobId}
      organizationSettings={organizationSettings || {}}
      onClose={onClose}
      onScheduleSuccess={onScheduleSuccess}
      initialScheduledAt={initialScheduledAt}
      initialDuration={initialDuration}
    />
  );
}
